import { mapActions } from "vuex";
import _ from "lodash";

export const formsNetworkMixin = {
  props: {
    /**
     * Name of the layer of the object for which the form was opened.
     */
    objectLayer: {
      type: String,
      required: true
    },

    /**
     * Data of selected object
     */
    objectData: {
      type: Object,
      required: false
    }
  },

  data() {
    return {
      /**
       * Data of the selected object's form structure
       */
      form: {
        type_name: undefined,
        endpoint: undefined,
        initials: undefined,
        permission: undefined,
        name_translation: undefined,
        local_storage_name: undefined,
        tabs: []
      },

      /**
       * Defines which expansion-panel panels are open
       */
      panel: [0],

      /**
       * List of materials
       */
      materialOptions: null,

      /**
       * List of form image fields
       */
      imagesList: [],

      /**
       * Images of the selected object
       */
      previousImages: []
    };
  },
  computed: {
    /**
     * Stores the initials corresponding to the current object layer.
     * @returns {string}
     */
    initials() {
      return this.form.initials + "_";
    },

    materialRequestQueryParameters() {
      return {};
    },

    objectUuid() {
      return this.objectData[this.initials + "uuid"];
    }
  },

  created() {
    this.structureAndPopulateForm();
  },

  methods: {
    ...mapActions([
      "FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE",
      "fetchStockMaterialsList"
    ]),

    /**
     * Gets the structure of the form from the backend and populates the objectData object.
     */
    async structureAndPopulateForm() {
      this.form = await this.FETCH_OBJECT_FORM_STRUCTURE_BY_OBJECT_TYPE({
        layer: this.objectLayer,
        needsTabs: true,
        needsFields: true
      });

      if (this.form.tabs.some(tab => tab.type === "photos")) {
        this.getImagesList();
        this.getPreviousImages();
      }
    },

    /**
     * Gets the (nested) objectData corresponding to a string key, with a new value
     * @param {String} varName - path to the variable we're trying to access.
     */
    getValueInObject(varName) {
      return _.get(this.objectData, varName);
    },

    /**
     * Returns the title corresponds to the selected object
     * @returns {string|*}
     */
    getTitle() {
      if (this.initials) {
        let label = this.getValueInObject(this.initials + "label");
        return label
          ? label
          : this.$i18n.tc(this.getTranslationKey(this.objectLayer), 1);
      } else {
        return "";
      }
    },

    /**
     * Get the translation of the layer
     *
     * @param {String} layer
     * @return {String} Returns the translation of the layer if it is filled
     */
    getTranslationKey(layer) {
      return this.$store.getters.getLayerTranslationKey(layer);
    },

    /**
     * Determines panel is disable
     * @param tab
     * @returns {boolean}
     */
    panelIsDisable(tab) {
      if (tab.type === "photos") {
        return tab.type === "photos" && this.previousImages.length === 0;
      } else if (tab.type === "form") {
        for (let field of tab.fields) {
          if (this.getValueInObject(field.var_name)) {
            return false;
          }
        }
        return true;
      }
    },
    /**
     * Determines panel is visible
     * @param tab
     * @returns {boolean}
     */
    panelIsVisible(tab) {
      return !(
        tab.label === "Accessories" ||
        tab.label === "Others" ||
        tab.label === "Masks" ||
        tab.code === "manhole_masks" ||
        tab.code === "manhole_asset_reservation" ||
        tab.code === "closure_splitters" ||
        tab.code === "closure_splicing" ||
        tab.code === "pathway_simplified_cables" ||
        tab.code === "pathway_asset_reservation" ||
        tab.code === "pole_pole" ||
        tab.label === "Pole" ||
        tab.label === "Material" ||
        tab.code === "pole_material" ||
        tab.label === "Terrain" ||
        tab.code === "pole_terrain" ||
        tab.label === "Angle" ||
        tab.code === "pole_angle" ||
        tab.code === "pole_asset_reservation" ||
        tab.code === "building_operator" ||
        tab.code === "building_typification" ||
        tab.type === "table" ||
        tab.code === "technical_local_olt_splicing" ||
        tab.code === "technical_local_olt_splitters" ||
        tab.code === "technical_local_fdp_splicing" ||
        tab.code === "technical_local_fdp_splitters" ||
        tab.code === "cable_pathways"
      );
    },

    ///////////////////////////////
    /* Stock Material methods */
    ///////////////////////////////

    /**
     * Get all material options
     * @returns {Promise<void>}
     */
    async loadMaterialOptions() {
      try {
        this.materialOptions = await this.fetchStockMaterialsList(
          this.materialRequestQueryParameters
        );
      } catch (e) {
        console.error(e);
      }
    },

    ////////////////////
    /* Photos methods */
    ////////////////////

    /**
     * Create a list of images to be used in the photos tab to generate the photo fields.
     */
    getImagesList() {
      let photosTab = this.form.tabs.find(tab => tab.type === "photos");
      let imagesList = [];
      for (const photoField of photosTab.fields) {
        imagesList.push({
          label: photoField.label,
          varName: photoField.var_name
        });
      }
      let extraImagesList = this.addExtraPhotosToImagesList();
      this.imagesList = imagesList.concat(extraImagesList);
    },

    /**
     * Generator of photos extra fields.
     * @returns {Array}
     */
    addExtraPhotosToImagesList() {
      let extraImagesList = [];
      for (let i = 1; i <= 10; i++) {
        extraImagesList.push({
          label: "Extra " + i,
          varName: "photoExtra" + i
        });
      }
      return extraImagesList;
    },

    /**
     * Get the label corresponding to the image by var_name
     * @param varName
     * @returns {*}
     */
    getImageLabel(varName) {
      return this.imagesList.find(img => img.varName === varName).label;
    },

    /**
     * Stores information about object images.
     */
    getPreviousImages() {
      this.previousImages = Array.from(this.objectData.images);
    },

    /**
     * Scroll the object to find the geom
     * @param object
     * @param field
     * @returns {{"type":"Point","coordinates":[-8.66135716438293,41.1562375075913]}}
     */
    searchObjectField(object, field) {
      for (const [key, value] of Object.entries(object)) {
        if (typeof value === "object" && value !== null) {
          let fieldFounded = this.searchObjectField(value, field);
          if (fieldFounded) return fieldFounded;
        }
        if (key.includes(field) && value) {
          return value;
        }
      }
    }
  }
};



// WEBPACK FOOTER //
// ./src/mixins/formsNetworkMixin.js